/**
 * Ledger: the customer's own ledger in TallyPrime for a period — opening balance, every voucher with its debit or
 * credit and the balance after it, closing balance — read on request through the office's connector (0038;
 * docs/product/statement/01-LEDGER-PLAN.md §8; owner's decisions §15).
 *
 * Opening the tab asks for the current financial year (the default, owner §15.4), once, when the office can
 * answer; a period is one press away, or chosen dates (at most 24 months). Before anyone waits, the page says why
 * it cannot be read now — the office computer out of touch, TallyPrime closed, the connector too old. While it is
 * read: "Asking TallyPrime on the office computer…", with Cancel. Afterwards: four figures, then the ledger — a table
 * where there is room, a list of entries on a phone — and when it was read. TallyPrime's own figures, checked twice
 * (opening and every line come to the closing, to the paisa: on the office computer and again on the server), never
 * a figure Timely made up. No narration, reference or bank: nothing about how the customer paid.
 *
 * Anyone who may open the account may read it (owner, §15.1); the spreadsheet needs Export reports (§15.2), and is
 * refused by the function that makes it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button, DrCr, Panel, Segmented, SkeletonRows, Table, Td, Th, RowHead, buttonClass, cx } from '@timely/ui';
import {
    LEDGER_PERIOD_WORDS, ledgerBillWords, ledgerPeriodProblem, ledgerPresets, ledgerSide, ledgerView,
    type LedgerPeriodKey, type LedgerRow, type LedgerView,
} from '@timely/domain';
import { useFormatters } from '../../book/useFormatters';
import { DateField } from '../../forms/DateField';
import { useActiveWorkspace } from '../../workspace/WorkspaceProvider';
import { paths } from '../../workspace/navigation';
import { exportLedgerCsv, mayExportStatement } from '../../receivables/statementAccess';
import { useLedgerRequest, type LedgerAvailability, type LedgerReady, type LedgerResult } from '../../receivables/useLedger';
import { useAccount } from './accountPage';
import { useTabTo } from './AccountTabs';

/** How many entries a long ledger shows before "Show all". */
export const FIRST_ENTRIES = 100;

const PERIOD_LABELS: Record<LedgerPeriodKey, string> = { this_fy: 'This year', last_fy: 'Last year', last_3_months: 'Last 3 months' };

/** Why the ledger cannot be read now, said before anybody waits for it (§10). */
const NOT_READY: Record<Exclude<LedgerReady, 'ready' | 'office_offline'>, string> = {
    no_connection: 'TallyPrime is not connected to Timely Payment just now, so the ledger can’t be read.',
    connector_too_old: 'The ledger needs the newer Timely Connector on the office computer.',
    tally_closed: 'TallyPrime isn’t open on the office computer, so the ledger can’t be read now.',
    company_not_loaded: 'TallyPrime on the office computer isn’t showing this company. Open it there, and the ledger can be read.',
};

export function CustomerLedger() {
    const page = useAccount();
    const tabTo = useTabTo();
    if (page.ledgerLoading) {
        return <Panel id="ledger-heading" headingLevel={3} title="Ledger"><SkeletonRows rows={5} /></Panel>;
    }
    // No Ledger to read here (a sheet's account, a company that does not read ledgers, an older connector): its address is Overview's.
    if (!page.ledger?.available) return <Navigate to={tabTo('overview')} replace />;
    return <LedgerPanel availability={page.ledger} />;
}

function LedgerPanel({ availability }: { availability: LedgerAvailability }) {
    const page = useAccount();
    const context = useActiveWorkspace();
    const { date } = useFormatters();
    const presets = useMemo(() => ledgerPresets(availability.today, availability.booksFrom), [availability.today, availability.booksFrom]);
    const first = presets[0]!;
    const [choice, setChoice] = useState<LedgerPeriodKey | 'dates'>(first.key ?? 'this_fy');
    const [from, setFrom] = useState(first.from);
    const [to, setTo] = useState(first.to);
    const lastSeen = (iso: string) => (date.inputValue(iso) === availability.today ? `at ${date.time(iso)}` : `on ${date.short(iso)} at ${date.time(iso)}`);
    const { state, ask, cancel } = useLedgerRequest(context.workspace.id, page.customer.id, { lastSeen, lastSeenAt: availability.lastSeenAt });

    // Opening the tab asks for this financial year, once, when the office can answer.
    const asked = useRef(false);
    useEffect(() => {
        if (asked.current || availability.ready !== 'ready') return;
        asked.current = true;
        void ask(first.from, first.to);
    }, [availability.ready, ask, first.from, first.to]);

    const choose = (key: LedgerPeriodKey | 'dates') => {
        setChoice(key);
        const preset = presets.find(p => p.key === key);
        if (!preset) return;
        setFrom(preset.from);
        setTo(preset.to);
        void ask(preset.from, preset.to);
    };
    const problem = choice === 'dates' ? ledgerPeriodProblem(from, to, availability.today, availability.booksFrom) : null;
    const view = useMemo(() => (state.result ? ledgerView(state.result) : null), [state.result]);
    const asking = state.phase === 'asking';
    const notReady = availability.ready !== 'ready';

    const tryAgain = async () => {
        await page.reloadLedger();
        if (state.from && state.to) void ask(state.from, state.to);
        else void ask(from, to);
    };

    return (
        <Panel
            id="ledger-heading" headingLevel={3} title="Ledger"
            description="From TallyPrime on the office computer, read when you ask. Timely does not change TallyPrime."
            actions={view && state.request && mayExportStatement(context) ? (
                <div className="flex items-center gap-2 flex-wrap">
                    <Link to={paths(context.workspace.slug).customerLedgerStatement(page.customer.id, state.request)} className={buttonClass({ variant: 'secondary', size: 'sm' })}>Statement PDF</Link>
                    <Button variant="secondary" size="sm" onClick={() => { exportLedgerCsv(context, view, page.customer.company); }}>Excel (CSV)</Button>
                </div>
            ) : undefined}
        >
            <div className="px-5 pb-4">
                <div className="flex items-center gap-3 flex-wrap">
                    <Segmented label="Period" value={choice} onChange={choose} className="flex-wrap"
                        options={[...presets.map(p => ({ value: p.key!, label: PERIOD_LABELS[p.key!] })), { value: 'dates' as const, label: 'Dates' }]} />
                    {choice !== 'dates' && state.from && state.to && (
                        <span className="text-secondary text-label-3 num">{date.day(state.from)} – {date.day(state.to)}</span>
                    )}
                </div>
                {choice === 'dates' && (
                    <form className="mt-3 flex items-end gap-3 flex-wrap" onSubmit={e => { e.preventDefault(); if (!problem) void ask(from, to); }}>
                        <div className="w-44"><DateField id="ledger-from" label="From" value={from} onChange={setFrom} /></div>
                        <div className="w-44"><DateField id="ledger-to" label="To" value={to} onChange={setTo} /></div>
                        <Button type="submit" variant="primary" disabled={problem !== null || asking}>Show the ledger</Button>
                        {problem && <p role="alert" className="basis-full text-secondary text-warn">{LEDGER_PERIOD_WORDS[problem]}</p>}
                    </form>
                )}
            </div>

            <div aria-live="polite" aria-busy={asking}>
                {asking ? (
                    <div className="border-t border-separator px-5 py-6 flex items-center gap-4 flex-wrap">
                        <span aria-hidden="true" className="size-2 bg-accent animate-pulse" />
                        <p className="text-body text-label">
                            {state.progress === 'reading' ? 'TallyPrime is reading the ledger on the office computer…' : 'Asking TallyPrime on the office computer…'}
                            <span className="block text-secondary text-label-3">Usually under a minute.</span>
                        </p>
                        <Button variant="quiet" size="sm" className="ml-auto" onClick={() => { void cancel(); }}>Cancel</Button>
                    </div>
                ) : state.phase === 'done' && view && state.result ? (
                    <LedgerShown view={view} result={state.result} />
                ) : state.phase === 'refused' ? (
                    <Message text={state.problem ?? ''} action={state.problem === 'Cancelled.' ? null : { label: 'Try again', onClick: () => { void tryAgain(); } }} />
                ) : notReady ? (
                    <Message
                        text={availability.ready === 'office_offline'
                            ? `The office computer was last in touch ${availability.lastSeenAt ? lastSeen(availability.lastSeenAt) : 'a while ago'}, so the ledger can’t be read now.`
                            : NOT_READY[availability.ready as keyof typeof NOT_READY]}
                        action={{ label: 'Check again', onClick: () => { void tryAgain(); } }} />
                ) : null}
            </div>
        </Panel>
    );
}

function Message({ text, action }: { text: string; action: { label: string; onClick: () => void } | null }) {
    return (
        <div className="border-t border-separator px-5 py-5 flex items-center gap-4 flex-wrap">
            <p className="text-body text-label-2 max-w-2xl">{text}</p>
            {action && <Button variant="secondary" size="sm" className="ml-auto" onClick={action.onClick}>{action.label}</Button>}
        </div>
    );
}

/** A balance with its side, as the books write it: "₹40,000.00 Dr"; nil is nil. */
function Balance({ paise, strong = false }: { paise: number; strong?: boolean }) {
    const { money } = useFormatters();
    const side = ledgerSide(paise);
    return (
        <span className={cx('num whitespace-nowrap', strong ? 'font-semibold text-label' : 'text-label-2')}>
            {money.precise(Math.abs(paise) / 100)}{side && <> <DrCr side={side} /></>}
        </span>
    );
}

function LedgerShown({ view, result }: { view: LedgerView; result: LedgerResult }) {
    const { money, date, count } = useFormatters();
    const [all, setAll] = useState(false);
    const rows = all ? view.rows : view.rows.slice(0, FIRST_ENTRIES);
    const agrees = result.syncedClosingPaise === null ? null : result.syncedClosingPaise === result.tallyClosingPaise;
    const amount = (paise: number) => (paise ? money.precise(paise / 100) : '');

    return (
        <div className="@container border-t border-separator">
            {/* The period's four figures: where it started, what moved each way, where it ended. */}
            <dl aria-label="The period in figures" className="grid grid-cols-2 @min-[40rem]:grid-cols-4 gap-x-6 gap-y-3 px-5 py-4">
                <Figure label={`Opening, ${date.day(view.from)}`}><Balance paise={view.openingPaise} strong /></Figure>
                <Figure label="Debits"><span className="num font-semibold text-label">{money.precise(view.debitsPaise / 100)}</span></Figure>
                <Figure label="Credits"><span className="num font-semibold text-label">{money.precise(view.creditsPaise / 100)}</span></Figure>
                <Figure label={`Closing, ${date.day(view.to)}`}><Balance paise={view.closingPaise} strong /></Figure>
            </dl>
            <p className="px-5 pb-3 text-caption text-label-3">
                Read from TallyPrime {date.inputValue(result.readAt) === date.todayValue() ? `at ${date.time(result.readAt)}` : `on ${date.short(result.readAt)}`}
                {' · '}{count(view.rows.length)} {view.rows.length === 1 ? 'entry' : 'entries'}
                {agrees === true && ' · TallyPrime’s balance today agrees with the last update'}
                {agrees === false && ' · TallyPrime has changed since the last update; the account will catch up at the next one'}
            </p>

            {view.rows.length === 0 ? (
                <p className="border-t border-separator px-5 py-4 text-secondary text-label-3">No entries in this period: the balance stayed as it was.</p>
            ) : <>
                {/* Where there is room: the ledger as a table, every figure in its own column. */}
                <div className="hidden @min-[40rem]:block border-t border-separator">
                    <Table caption="The ledger, oldest first" aria-label="Ledger entries">
                        <thead>
                            <tr>
                                <Th>Date</Th>
                                <Th>Voucher</Th>
                                <Th hideBelow="md">No.</Th>
                                <Th hideBelow="lg">Bills</Th>
                                <Th numeric>Debit</Th>
                                <Th numeric>Credit</Th>
                                <Th numeric>Balance</Th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="bg-card-2/60">
                                <RowHead className="text-secondary font-normal text-label-2 num whitespace-nowrap">{date.day(view.from)}</RowHead>
                                <Td className="text-secondary font-semibold text-label" colSpan={1}>Opening balance</Td>
                                <Td hideBelow="md" />
                                <Td hideBelow="lg" />
                                <Td numeric />
                                <Td numeric />
                                <Td numeric className="text-secondary"><Balance paise={view.openingPaise} strong /></Td>
                            </tr>
                            {rows.map((r, i) => <TableRow key={`${r.date}:${r.number ?? ''}:${i}`} row={r} amount={amount} />)}
                            {all || view.rows.length <= FIRST_ENTRIES ? (
                                <tr className="bg-card-2/60">
                                    <RowHead className="text-secondary font-normal text-label-2 num whitespace-nowrap">{date.day(view.to)}</RowHead>
                                    <Td className="text-secondary font-semibold text-label">Closing balance</Td>
                                    <Td hideBelow="md" />
                                    <Td hideBelow="lg" />
                                    <Td numeric className="text-secondary font-semibold text-label">{money.precise(view.debitsPaise / 100)}</Td>
                                    <Td numeric className="text-secondary font-semibold text-label">{money.precise(view.creditsPaise / 100)}</Td>
                                    <Td numeric className="text-secondary"><Balance paise={view.closingPaise} strong /></Td>
                                </tr>
                            ) : null}
                        </tbody>
                    </Table>
                </div>

                {/* A phone: one entry a row — what and when on the left, the amount and the balance after it on the right. */}
                <ol aria-label="Ledger entries" className="@min-[40rem]:hidden border-t border-separator divide-y divide-separator">
                    <li className="px-5 py-2.5 flex items-baseline justify-between gap-3 bg-card-2/60">
                        <span className="text-secondary font-semibold text-label">Opening, {date.day(view.from)}</span>
                        <Balance paise={view.openingPaise} strong />
                    </li>
                    {rows.map((r, i) => <ListRow key={`${r.date}:${r.number ?? ''}:${i}`} row={r} />)}
                    {(all || view.rows.length <= FIRST_ENTRIES) && (
                        <li className="px-5 py-2.5 flex items-baseline justify-between gap-3 bg-card-2/60">
                            <span className="text-secondary font-semibold text-label">Closing, {date.day(view.to)}</span>
                            <Balance paise={view.closingPaise} strong />
                        </li>
                    )}
                </ol>

                {view.rows.length > FIRST_ENTRIES && (
                    <div className="px-5 py-2.5 border-t border-separator">
                        <Button variant="quiet" size="sm" aria-expanded={all} onClick={() => setAll(!all)}>
                            {all ? `Show the first ${count(FIRST_ENTRIES)} only` : `Show all ${count(view.rows.length)} entries`}
                        </Button>
                    </div>
                )}
            </>}
        </div>
    );
}

function Figure({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="min-w-0">
            <dt className="text-caption font-semibold text-label-3">{label}</dt>
            <dd className="mt-0.5 text-subhead">{children}</dd>
        </div>
    );
}

function TableRow({ row, amount }: { row: LedgerRow; amount: (paise: number) => string }) {
    const { date } = useFormatters();
    const bills = ledgerBillWords(row.bills);
    return (
        <tr>
            <RowHead className="text-secondary font-normal text-label-2 num whitespace-nowrap">{date.day(row.date)}</RowHead>
            <Td className="text-secondary text-label">
                <span className="break-words">{row.type}</span>
                {/* Narrower than the No. and Bills columns: they go under the voucher's type. */}
                {(row.number || bills) && (
                    <span className="block @min-[56rem]:hidden text-caption text-label-3 break-words">
                        {[row.number ? `No. ${row.number}` : '', bills].filter(Boolean).join(' · ')}
                    </span>
                )}
            </Td>
            <Td hideBelow="md" className="text-secondary text-label-2 num whitespace-nowrap">{row.number ?? ''}</Td>
            <Td hideBelow="lg" className="text-secondary text-label-2 break-words">{bills}</Td>
            <Td numeric className="text-secondary text-label">{amount(row.debitPaise)}</Td>
            <Td numeric className="text-secondary text-label">{amount(row.creditPaise)}</Td>
            <Td numeric className="text-secondary"><Balance paise={row.balancePaise} /></Td>
        </tr>
    );
}

function ListRow({ row }: { row: LedgerRow }) {
    const { money, date } = useFormatters();
    const bills = ledgerBillWords(row.bills);
    const debit = row.debitPaise > 0;
    const moved = debit ? row.debitPaise : row.creditPaise;
    return (
        <li className="px-5 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5">
            <span className="text-secondary text-label break-words">
                <span className="num text-label-2">{date.day(row.date)}</span> · {row.type}{row.number ? ` ${row.number}` : ''}
            </span>
            <span className="text-secondary font-semibold text-label num text-right whitespace-nowrap">
                {moved ? <>{money.precise(moved / 100)} <span className="text-caption font-semibold text-label-3">{debit ? 'Dr' : 'Cr'}</span></> : '—'}
            </span>
            <span className="text-caption text-label-3 break-words">{bills}</span>
            <span className="text-caption text-label-3 text-right whitespace-nowrap">Balance <Balance paise={row.balancePaise} /></span>
        </li>
    );
}
